import { db, Document, getDocumentTitle } from "../db";
import { MouseEvent } from "react";
import SaveIcon from "@mui/icons-material/DownloadForOffline";
import QueuedIcon from "@mui/icons-material/CloudQueue";
import TickIcon from "@mui/icons-material/DownloadDone";
import { addToDownloadQueue, useFileDownloadStatus } from "../providers/FileDownloader";
import { DocumentAndOptionalScore } from "../pages/App";
import Typography from "@mui/material/Typography";

import pptSvg from "../file_icons/pptx.svg";
import binSvg from "../file_icons/bin.svg";
import docSvg from "../file_icons/doc.svg";
import docmSvg from "../file_icons/docm.svg";
import docxSvg from "../file_icons/docx.svg";
import dotxSvg from "../file_icons/dotx.svg";
import jsonSvg from "../file_icons/json.svg";
import pdfSvg from "../file_icons/pdf.svg";
import ppsxSvg from "../file_icons/ppsx.svg";
import pptxSvg from "../file_icons/pptx.svg";
import xlsSvg from "../file_icons/xls.svg";
import xlsmSvg from "../file_icons/xlsm.svg";
import xlsxSvg from "../file_icons/xlsx.svg";
import xltxSvg from "../file_icons/xltx.svg";
import zipSvg from "../file_icons/zip.svg";
import { Box, CircularProgress } from "@mui/material";
import { useAppConfiguration } from "../providers/AppConfiguration";

const defaultIcon = binSvg;
const fileExtIcons: Record<string, string> = {
  ppt: pptSvg,
  bin: binSvg,
  doc: docSvg,
  docm: docmSvg,
  docx: docxSvg,
  dotx: dotxSvg,
  json: jsonSvg,
  pdf: pdfSvg,
  ppsx: ppsxSvg,
  pptx: pptxSvg,
  xls: xlsSvg,
  xlsm: xlsmSvg,
  xlsx: xlsxSvg,
  xltx: xltxSvg,
  zip: zipSvg,
};

export type Filter = Record<string, string>;

export interface DocumentListProps {
  docs: DocumentAndOptionalScore[];
  onFileOpened: (doc: Document, target: string) => void;
}

interface SimpleListItemProps {
  doc: DocumentAndOptionalScore;
  titleField: string;
  downloading: boolean;
  percentComplete?: number;
}

function SimpleListItem(props: SimpleListItemProps) {
  const { doc, titleField } = props;
  const [title, ext] = getDocumentTitle(doc, titleField);

  const icon = doc.download_order ? <QueuedIcon /> : doc.stored_etag ? <TickIcon /> : <SaveIcon />;

  return (
    <div className="documentItem actionTarget" data-id={doc.id}>
      <img src={fileExtIcons[ext] ?? defaultIcon} style={{ width: "32px" }} alt={ext} />
      <div className="documentName">
        <div>{title}</div>
        {doc.score ? <Typography variant="caption">Score: {doc.score}</Typography> : ""}
      </div>
      <button className="documentDownloadButton undecorated actionTarget">
        {props.downloading ? (
          <Box sx={{ position: "relative", width: 20, height: 20 }}>
            <CircularProgress
              sx={(theme) => ({
                position: "absolute",
                top: 0,
                left: 0,
                color: theme.palette.grey[200],
                ...theme.applyStyles("dark", {
                  color: theme.palette.grey[800],
                }),
              })}
              variant="determinate"
              thickness={6}
              size={20}
              value={100}
            />

            <CircularProgress
              key={doc.key}
              sx={(theme) => ({
                position: "absolute",
                top: 0,
                left: 0,
                transition: "none",
                // color: theme.palette.grey[200],
                // ...theme.applyStyles("dark", {
                //   color: theme.palette.grey[800],
                // }),
              })}
              size={20}
              thickness={6}
              disableShrink
              variant={props.percentComplete ? "determinate" : "indeterminate"}
              value={props.percentComplete}
            />
          </Box>
        ) : (
          icon
        )}
      </button>
    </div>
  );
}

export default function DocumentList(props: DocumentListProps) {
  const { docs } = props;

  const downloadStatus = useFileDownloadStatus();
  const appConfiguration = useAppConfiguration();

  async function onDocumentClicked(evt: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) {
    const tgt: HTMLElement | null = evt.target as HTMLElement;

    const docItem = tgt?.closest(".documentItem");
    const downloadButton = tgt.closest(".documentDownloadButton");

    if (docItem) {
      let action = downloadButton ? "download" : "open";
      const id = parseInt(docItem?.getAttribute("data-id") as string);
      const doc = await db.documents.get(id);

      if (doc) {
        console.log(action, doc?.key);
        switch (action) {
          case "download":
            addToDownloadQueue([doc]);
            break;
          case "open":
            const newTab = evt.shiftKey || window.navigator.userAgent.indexOf("Mac OS") !== -1 ? evt.metaKey : evt.ctrlKey;
            props.onFileOpened(doc, newTab ? "_blank" : "_self");
            break;
        }
      }
    }
  }

  return (
    <div style={{ width: "100%" }} onClick={(evt) => onDocumentClicked(evt)}>
      {docs.map((doc) => {
        const downloading = downloadStatus?.key === doc.key;
        const percentComplete = downloading ? downloadStatus?.percentCompleted : undefined;
        return (
          <SimpleListItem
            doc={doc}
            titleField={appConfiguration.titleField}
            key={doc.id}
            downloading={downloading}
            percentComplete={percentComplete}
          />
        );
      })}
    </div>
  );
}
